import axios from 'axios'
import {formatApiError} from '../../../shared/utils/apiErrorMessage'

const API_URL = process.env.REACT_APP_API_URL

export type RoleRecord = {
  id: number
  roleType?: string
  /** Behavioral template when roleType is CUSTOM */
  baseRoleType?: string | null
  /** Same as server effective behavior; preferred for labels */
  effectiveBehaviorType?: string | null
  displayName?: string | null
  code?: string | null
  description?: string | null
  systemRole?: boolean
  customRole?: boolean
  /** False for platform SUPER_ADMIN role (do not assign from normal UI). */
  assignable?: boolean
  /** Non-null = company-owned custom role; omitted/null with customRole = global custom (superadmin). */
  companyGroupAdminId?: number | null
  /** Resolved display name for the owning company group; null for global roles. */
  companyGroupName?: string | null
  active?: boolean
  defaultDataScope?: string | null
  createdAt?: string | null
}

export type CreateRoleFullPayload = {
  displayName: string
  code?: string | null
  /** Behavioral template (e.g. COMPANY_ADMIN); stored as base_role_type */
  baseRoleType: string
  description?: string | null
  defaultDataScope?: string | null
  active?: boolean
  permissionMode: 'BLANK' | 'COPY' | 'MANUAL'
  copyFromRoleId?: number | null
  permissionNames?: string[] | null
  /** Superadmin / global admin only: null = global custom role; id = company-scoped. Omitted for company users (server uses actor scope). */
  companyGroupAdminId?: number | null
}

/** Legacy POST /api/roles (template or copy-from in one body). */
export type CreateRolePayload = {
  displayName: string
  code?: string | null
  /** Omitted = server defaults to COMPANY_ADMIN */
  baseRoleType?: string | null
  description?: string | null
  defaultDataScope?: string | null
  active?: boolean
  copyFromRoleId?: number | null
  /** Superadmin: required. Scoped admins: omit — server uses actor company group. */
  companyGroupAdminId?: number | null
}

export type UpdateRolePayload = {
  displayName?: string | null
  description?: string | null
  active?: boolean | null
  code?: string | null
  baseRoleType?: string | null
  defaultDataScope?: string | null
}

export type RolePermissionRecord = {
  id?: number | null
  roleId?: number | null
  permissionId?: number | null
  permission?: { id: number; name: string; description?: string | null } | null
  roles?: { id: number; roletype?: string } | null
  source?: string | null
}

const getErrorMessage = (error: any, fallback: string): string =>
  formatApiError(error, fallback)

const normalizeRoleListResponse = (raw: any): RoleRecord[] => {
  const items =
    raw?.content ??
    raw?.items ??
    raw?.data ??
    raw
  return Array.isArray(items) ? items : []
}

export const getRolesList = async (
  activeOnly = false,
  companyGroupAdminId?: number | null
): Promise<RoleRecord[]> => {
  try {
    const res = await axios.get<RoleRecord[]>(`${API_URL}/roles`, {
      params: {
        activeOnly,
        ...(companyGroupAdminId != null && Number.isFinite(companyGroupAdminId)
          ? { companyGroupAdminId }
          : {}),
      },
    })
    return normalizeRoleListResponse(res.data)
  } catch (error: any) {
    throw new Error(getErrorMessage(error, 'Failed to load roles'))
  }
}

export const createCustomRoleWithPermissions = async (body: CreateRoleFullPayload): Promise<RoleRecord> => {
  try {
    const res = await axios.post<RoleRecord>(`${API_URL}/roles/create-with-permissions`, body)
    return res.data
  } catch (error: any) {
    throw new Error(getErrorMessage(error, 'Failed to create role'))
  }
}

export const createCustomRole = async (body: CreateRolePayload): Promise<RoleRecord> => {
  try {
    const res = await axios.post<RoleRecord>(`${API_URL}/roles`, body)
    return res.data
  } catch (error: any) {
    throw new Error(getErrorMessage(error, 'Failed to create role'))
  }
}

export const updateRole = async (id: number, body: UpdateRolePayload): Promise<RoleRecord> => {
  try {
    const res = await axios.put<RoleRecord>(`${API_URL}/roles/${id}`, body)
    return res.data
  } catch (error: any) {
    throw new Error(getErrorMessage(error, 'Failed to update role'))
  }
}

export const patchRoleActive = async (id: number, active: boolean): Promise<RoleRecord> => {
  try {
    const res = await axios.patch<RoleRecord>(`${API_URL}/roles/${id}/active`, { active })
    return res.data
  } catch (error: any) {
    throw new Error(getErrorMessage(error, 'Failed to update role status'))
  }
}

export type ReplaceRolePermissionsPayload = {
  permissionIds?: number[]
  permissionNames?: string[]
}

export type ReplaceRolePermissionsResult = {
  permissions: RolePermissionRecord[]
  staleRemovedCount?: number
  droppedParentUnavailableCount?: number
}

export const replaceRolePermissionsForRole = async (
  roleId: number,
  payload: string[] | ReplaceRolePermissionsPayload
): Promise<ReplaceRolePermissionsResult> => {
  const body: ReplaceRolePermissionsPayload = Array.isArray(payload)
    ? {permissionNames: payload}
    : payload
  try {
    const res = await axios.put<RolePermissionRecord[] | ReplaceRolePermissionsResult>(
      `${API_URL}/role-permissions/roles/${roleId}`,
      body
    )
    const data = res.data
    if (Array.isArray(data)) {
      return {permissions: data}
    }
    return {
      permissions: data?.permissions ?? [],
      staleRemovedCount: data?.staleRemovedCount,
      droppedParentUnavailableCount: data?.droppedParentUnavailableCount,
    }
  } catch (error: any) {
    throw new Error(getErrorMessage(error, 'Failed to update role permissions'))
  }
}

export const copyRolePermissionsFrom = async (targetRoleId: number, sourceRoleId: number): Promise<void> => {
  try {
    await axios.post(`${API_URL}/roles/${targetRoleId}/copy-permissions-from/${sourceRoleId}`)
  } catch (error: any) {
    throw new Error(getErrorMessage(error, 'Failed to copy permissions'))
  }
}

export const getRolePermissionsList = async (): Promise<RolePermissionRecord[]> => {
  try {
    const res = await axios.get<RolePermissionRecord[]>(`${API_URL}/role-permissions`)
    return res.data || []
  } catch (error: any) {
    throw new Error(getErrorMessage(error, 'Failed to load role permissions'))
  }
}

export const assignRolePermission = async (roleId: number, permissionName: string): Promise<RolePermissionRecord> => {
  try {
    const res = await axios.post<RolePermissionRecord>(
      `${API_URL}/role-permissions/create`,
      null,
      { params: { roleId, permissionName } }
    )
    return res.data
  } catch (error: any) {
    throw new Error(getErrorMessage(error, 'Failed to assign permission to role'))
  }
}

export const deleteRolePermission = async (id: number): Promise<void> => {
  try {
    await axios.delete(`${API_URL}/role-permissions/delete/${id}`)
  } catch (error: any) {
    throw new Error(getErrorMessage(error, 'Failed to remove permission from role'))
  }
}
